import { FC } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, useFieldArray, FormProvider, FieldPath } from 'react-hook-form';
import useReactRouter from 'use-react-router';

import Button from 'jbc-front/components/Button';
import { ButtonRow } from 'jbc-front/components/presenters/layout/ButtonRow';

import { useEmployeeMutation as useMutation } from '@jbc-year-end-adj/2024/hooks/graphql/useMutation';
import { eachValidationErrorsForHookFrom, ServerValidationError } from '@jbc-year-end-adj/2024/hooks/graphql/error/ServerValidationError';

import { DeletableSection } from '@jbc-year-end-adj/2024/components/ui/DeletableSection';
import { FormSection } from '@jbc-year-end-adj/2024/components/form/FormSection';
import { FormAddButton } from '@jbc-year-end-adj/2024/components/form/FormAddButton';
import { Input } from '@jbc-year-end-adj/2024/components/form/Input';
import { FileField } from '@jbc-year-end-adj/2024/components/react-hook-form/FileField';
import { FilePreview } from '@jbc-year-end-adj/2024/features/FilePreview';
import { AmountField } from '@jbc-year-end-adj/2024/components/react-hook-form/AmountField';
import { SelectField } from '@jbc-year-end-adj/2024/components/react-hook-form/SelectField';

import { schema, Schema, generateDefaultValues, socialInsuranceObj } from './schema';
import { ANSWER_SOCIAL_INSURANCES, convertFormDataToQueryVariables } from './mutation';
import { Request } from './query';

const socialInsuranceTypes = [
  { value: 'national_pension', label: '国民年金' },
  { value: 'national_insurance', label: '国民年金基金' },
  { value: 'national_health_insurance', label: '国民健康保険' },
  { value: 'elderly_medical_insurance', label: '後期高齢者医療保険' },
  { value: 'national_nursing_medical_insurance', label: '介護保険' },
  { value: 'voluntary_continued_insurance', label: '任意継続保険' },
  { value: 'farmers_pension', label: '農業者年金' }
];

type FormProps = {
  request: Request;
  imageSetting: 'none' | 'optional' | 'required';
};

export const Form: FC<FormProps> = ({ request, imageSetting }) => {
  const [update, { loading }] = useMutation(ANSWER_SOCIAL_INSURANCES, {
    refetchQueries: ['clientSettings', 'questionGroups', 'socialInsrances']
  });
  const { history } = useReactRouter();

  const imageRequired = imageSetting === 'required';
  const methods = useForm<Schema>({
    defaultValues: generateDefaultValues(request.profile),
    resolver: yupResolver(schema(imageRequired)),
    mode: 'onSubmit'
  });

  const {
    handleSubmit,
    control,
    register,
    formState: { errors },
    setValue,
    setError,
    watch
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'socialInsurances'
  });

  const onSubmit = async (data: Schema) => {
    const variables = convertFormDataToQueryVariables(data);
    try {
      await update({ variables });
      history.push('/employee_input');
    } catch (err) {
      if (err instanceof ServerValidationError) {
        eachValidationErrorsForHookFrom(err.validationErrors, schema(imageRequired), data, (path, error) => {
          setError(path as FieldPath<Schema>, error);
        });
        return;
      }
      throw err;
    }
  };

  return (
    <FormProvider {...methods}>
      <form>
        <FormSection>
          {fields.map((field, index) => {
            const image = watch(`socialInsurances.${index}.image`);
            return (
              <DeletableSection key={field.id}>
                <DeletableSection.Header onDelete={() => remove(index)}>社会保険（{index + 1}）</DeletableSection.Header>
                <DeletableSection.Body>
                  <FormSection>
                    <SelectField
                      label="社会保険の種類"
                      name={`socialInsurances.${index}.insuranceType`}
                      options={socialInsuranceTypes}
                      required
                    />
                    <Input
                      label="保険料支払先の名称"
                      placeholder="例：日本年金機構　等"
                      required
                      {...register(`socialInsurances.${index}.name`)}
                      error={errors.socialInsurances && errors.socialInsurances[index]?.name?.message}
                      isError={errors.socialInsurances && !!errors.socialInsurances[index]?.name?.message}
                    />
                    <Input
                      label="保険料を負担することになっている人 氏名"
                      note="保険料控除証明書に記載された被保険者の氏名を記入してください。"
                      placeholder="例：ジョブカン　二郎"
                      required
                      {...register(`socialInsurances.${index}.nameOfPayer`)}
                      error={errors.socialInsurances && errors.socialInsurances[index]?.nameOfPayer?.message}
                      isError={errors.socialInsurances && !!errors.socialInsurances[index]?.nameOfPayer?.message}
                    />
                    <AmountField
                      label="本年中に支払った金額"
                      required
                      placeholder="例：100000"
                      {...register(`socialInsurances.${index}.amount`)}
                      error={errors.socialInsurances && errors.socialInsurances[index]?.amount?.message}
                      isError={errors.socialInsurances && !!errors.socialInsurances[index]?.amount?.message}
                    />
                    {imageSetting !== 'none' && (
                      <FileField
                        label="社会保険の証明書画像"
                        note="管理者が確認できるよう保険料控除証明書の画像（対応可能形式：jpg, jpeg, gif, png, pdf）をアップロードしてください。原本は必ず会社に提出してください。"
                        name={`socialInsurances.${index}.image`}
                        required={imageSetting === 'required'}
                        error={errors.socialInsurances && errors.socialInsurances[index]?.image?.message}
                      >
                        {image && (
                          <FileField.FileContainer>
                            <FileField.File file={image} onRemove={() => setValue(`socialInsurances.${index}.image`, null)} />
                            <FilePreview file={image} />
                          </FileField.FileContainer>
                        )}
                      </FileField>
                    )}
                  </FormSection>
                </DeletableSection.Body>
              </DeletableSection>
            );
          })}
          <FormAddButton onClick={() => append(socialInsuranceObj)}>社会保険を追加</FormAddButton>

          <ButtonRow>
            <Button primary onClick={handleSubmit(onSubmit)} disabled={loading}>
              次へ
            </Button>
          </ButtonRow>
        </FormSection>
      </form>
    </FormProvider>
  );
};
